import { CONVERSATION_MESSAGE, CONVERSATION_MESSAGE_BULK } from '@/config/api/api-routes';
import { Message } from '@/features/messages';
import { http } from '@/shared/api/http';
import type { ResultApi } from '@/shared/types/result-api';
import type { MessagesQueryParams, MessagesResponse } from '../models/message-pagination';

export interface LoadMessagesParams {
  conversationId: string;
  pageSize?: number;
  fromMessageId?: string | null;
  direction?: 'OLDER' | 'NEWER';
  signal?: AbortSignal;
}

const DEFAULT_PAGE_SIZE = 50;

/**
 * Loads messages from the API for a given conversation, including their parent messages.
 * Optimizes performance by loading only unique parent messages not already present in the response.
 * @param params - Parameters for loading messages
 * @returns Promise resolving to an array of Message objects
 * @throws Error if API call fails
 */
export async function loadMessages(params: LoadMessagesParams): Promise<Message[]> {
  try {
    const queryParams: MessagesQueryParams = {
      pageSize: params.pageSize || DEFAULT_PAGE_SIZE,
      fromMessageId: params.fromMessageId || null,
      direction: params.direction || 'OLDER'
    };
    const response = await http.get<ResultApi<MessagesResponse>>(
      CONVERSATION_MESSAGE.replace(':conversationId', params.conversationId),
      {
        params: queryParams,
        signal: params.signal
      }
    );

    const loadedMsg = response.data.data?.messages || [];

    // Collect unique parent message IDs that are not already in the loaded messages
    const loadedMsgIds = new Set(loadedMsg.map((msg) => msg.messageId));
    const parentMsgIds = [
      ...new Set(loadedMsg.filter((msg) => msg.parentMessageId && !msg.isRevoked).map((msg) => msg.parentMessageId!))
    ];

    const parentLoadeds = loadedMsg.map((msg) => new Message(msg));

    const needLoadMsgIds = parentMsgIds.filter((id) => !loadedMsgIds.has(id));
    const parentLoadMore = await loadMessageFromIds(params.conversationId, needLoadMsgIds);
    const parentMsgs = [...parentLoadMore, ...parentLoadeds];

    // Filter out the message with fromMessageId if reset is false and map to Message objects
    const messages = loadedMsg.map((msgData) => {
      const item = parentMsgs.find((m) => m.messageId === msgData.parentMessageId);
      return new Message(msgData, item);
    });

    return params.direction === 'NEWER' ? messages : messages.reverse();
  } catch (error) {
    return [];
  }
}

/**
 * Loads multiple messages by their IDs in a single bulk API call for efficiency.
 * @param convId - The conversation ID
 * @param messageIds - Array of message IDs to load
 * @returns Promise resolving to an array of Message objects
 * @throws Error if API call fails
 */
export async function loadMessageFromIds(convId: string, messageIds: string[]): Promise<Message[]> {
  if (messageIds.length === 0) return [];

  try {
    // Optimize by ensuring unique IDs to avoid redundant API payload
    const uniqueMessageIds = [...new Set(messageIds)];

    const response = await http.post<ResultApi<MessagesResponse>>(
      CONVERSATION_MESSAGE_BULK.replace(':conversationId', convId),
      {
        messageIds: uniqueMessageIds
      }
    );

    return response.data.data?.messages.map((messageData) => new Message(messageData)) || [];
  } catch (error) {
    return [];
  }
}
